import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Can } from '@/components/can';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import { StatusBadge } from '@/components/status-badge';
import { QrDisplay } from '@/components/qr-display';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { usePayment } from '@/hooks/use-payment';
import { useCancelPayment } from '@/hooks/use-cancel-payment';
import { formatAmount, formatDate, getTimeRemaining } from '@/lib/utils';

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-2.5 last:border-0">
      <span className="shrink-0 text-sm text-slate-500">{label}</span>
      <span className="text-right text-sm font-medium text-slate-900">{value}</span>
    </div>
  );
}

export function PaymentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: payment, isLoading, error } = usePayment(id!);
  const cancelMutation = useCancelPayment(id!);
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    if (!payment || payment.status !== 'PENDING' || !payment.qrExpiresAt) return;

    const tick = () => setTimeRemaining(getTimeRemaining(payment.qrExpiresAt));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [payment]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <div className="text-sm text-slate-400">Loading payment details...</div>
      </div>
    );
  }

  if (error || !payment) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <div className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">
          {error instanceof Error ? error.message : 'Payment not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/payments')}>
          <ArrowLeft className="h-4 w-4" /> Back to Payments
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <h2 className="text-2xl font-bold text-slate-900">Payment Detail</h2>
        <StatusBadge status={payment.status} className="px-3 py-1 text-sm" />
      </div>

      {payment.status === 'PENDING' && (
        <div className="flex items-center gap-2 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          QR expires in: <strong>{timeRemaining}</strong>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Payment Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-slate-100">
                <DetailRow
                  label="Bill Reference"
                  value={<span className="font-mono text-xs">{payment.billReference}</span>}
                />
                <DetailRow label="Bank" value={payment.bank} />
                <DetailRow
                  label="Amount"
                  value={
                    <span className="font-semibold tabular-nums">
                      {formatAmount(payment.amountCents)} {payment.currency}
                    </span>
                  }
                />
                <DetailRow label="Merchant" value={payment.merchantName ?? '—'} />
                <DetailRow label="Status" value={<StatusBadge status={payment.status} />} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Timestamps</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-slate-100">
                <DetailRow label="Created" value={formatDate(payment.createdAt)} />
                <DetailRow label="Updated" value={formatDate(payment.updatedAt)} />
                <DetailRow label="Paid At" value={payment.paidAt ? formatDate(payment.paidAt) : '—'} />
                <DetailRow label="QR Expires" value={formatDate(payment.qrExpiresAt)} />
              </div>
            </CardContent>
          </Card>

          <Can permission="paynow:payments:cancel">
            {payment.status === 'PENDING' && (
              <Card className="border-red-200">
                <CardContent className="pt-6">
                  {cancelMutation.error && (
                    <p className="mb-3 text-sm text-red-600">
                      {cancelMutation.error instanceof Error ? cancelMutation.error.message : 'Failed to cancel payment'}
                    </p>
                  )}
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={() => cancelMutation.mutate()}
                    disabled={cancelMutation.isPending}
                  >
                    {cancelMutation.isPending ? 'Cancelling...' : 'Cancel Payment'}
                  </Button>
                </CardContent>
              </Card>
            )}
          </Can>
        </div>

        <QrDisplay qrImageBase64={payment.qrImageBase64} qrPayload={payment.qrPayload} />
      </div>
    </div>
  );
}
